
import { z } from 'zod';
import {
  CharacterPlannedArcV2Schema,
  CharacterSnapshotV2Schema,
} from './character-sub-entities-v2';

// =============================================================================
// CHARACTER
// =============================================================================

export type ImportType = 'barebone' | 'full' | 'continued';

/**
 * The main container for a Character.
 * This is a lightweight entity that holds metadata and a timeline of snapshots.
 */
export const CharacterV2Schema = z.object({
  id: z.string().describe("The unique, permanent ID for this character."),
  name: z.string().describe("The character's proper name, without titles or honorifics. Titles and nicknames belong in aliases."),
  aliases: z.array(z.string()).optional().describe("Alternative names, nicknames, or titles for the character (e.g., 'Meg' for Margaret, 'The General')."),
  plannedPlotRole: z.string().describe("A one-sentence summary of the character's core identity, motivation, and role in the plot."),
  category: z.enum(['major', 'minor', 'extra']).describe("The character's overall importance to the story."),
  startingSnapshot: CharacterSnapshotV2Schema,

  // The complete timeline of the character's state across all chapters.
  snapshots: z.array(CharacterSnapshotV2Schema).describe("The array of all snapshots, creating a complete timeline of the character's journey."),

  // A pointer to the most recent state for quick access.
  latestSnapshotId: z.string().optional().describe("The ID of the most recent snapshot in the timeline."),

  // The planned developmental arc for the character.
  plannedCharacterArc: z.lazy(() => CharacterPlannedArcV2Schema.optional()),
  
  // -- NEW --
  inspirationIds: z.array(z.string()).optional().describe("An array of IDs pointing to inspiration profiles in the project's characterInspirations subcollection."),

  // Static metadata that doesn't change from chapter to chapter.
  notes: z.string().optional().describe("Author's private notes about the character."),
  mainImageId: z.string().optional().describe("ID of the resource used as the character's main portrait."),
});

/**
 * Minimal character reference for foreign key relationships.
 * Use when you just need to identify a character.
 *
 * Example: "This scene involves these characters: [ref1, ref2]"
 */
export const CharacterRefV2Schema = z.object({
  id: z.string().describe("Unique identifier for the character."),
  name: z.string().describe("The character's name."),
}).describe("Minimal character reference containing only ID and name, used for foreign key relationships and basic character identification.");


/**
 * Character data for basic AI context (ideation, planning).
 * Use when the AI doesn't need full personality/physical details.
 *
 * Use when: Plot generation, character ideation, high-level planning
 */
export const CharacterBasicV2Schema = z.object({
  name: z.string().describe("The character's proper name only (e.g., 'Frank Miller', not 'Captain Frank Miller'). Titles, nicknames, and epithets belong in aliases."),
  aliases: z.array(z.string()).optional().describe("Alternative names, nicknames, or titles for the character (e.g., 'Captain', 'The Tank', 'Meg' for Margaret)."),
  category: z.enum(['major', 'minor', 'extra']).describe("The character's importance level in the narrative."),
  plotRole: z.string().describe("A summary of the character's core identity, motivation, and role in the story."),
}).describe("Basic character data for high-level AI context during ideation and planning, without detailed personality or physical descriptions.");


/**
 * Character data for AI context in writing/drafting agents.
 * Includes all fields relevant for narrative generation.
 *
 * Use when: Passing characters to chapter-drafter, scene-generator, etc.
 */
export const CharacterContextV2Schema = z.object({
  id: z.string().describe("Unique identifier for the character."),
  name: z.string().describe("The character's proper name, without titles or honorifics. Titles and nicknames are in aliases."),
  aliases: z.array(z.string()).optional().describe("Alternative names, nicknames, or titles for the character (e.g., 'Captain', 'The Tank', 'Meg' for Margaret)."),
  age: z.string().optional().describe("The character's age or age range."),
  category: z.enum(['major', 'minor', 'extra']).describe("The character's importance level in the narrative."),
  plotRole: z.string().describe("A summary of the character's core identity, motivation, and role in the story."),
  personality: z.string().optional().describe("Description of the character's personality traits and behavioral patterns."),
  characterVoice: z.string().optional().describe("How the character speaks: speech patterns, vocabulary, verbal tics, formality level, and distinctive dialogue traits."),
  physicalDescription: z.string().optional().describe("Physical appearance and distinguishing features of the character."),
  clothing: z.string().optional().describe("Description of the character's clothing and accessories."),
  coreValues: z.string().optional().describe("The character's fundamental beliefs, principles, and values."),
}).describe("Character data optimized for AI context in writing and drafting agents, containing all fields relevant for narrative generation.");


/**
 * NEW: Character schema for defining their role and arc within a plot outline.
 * Used for plot generation and character extraction.
 */
export const CharacterRoleV2Schema = z.object({
  name: z.string().describe("The character's name."),
  category: z.enum(['major', 'minor', 'extra']).describe("The character's importance level in the narrative."),
  plotRole: z.string().describe("A summary of the character's purpose and influence on the plot."),
  plannedCharacterArc: z.lazy(() => CharacterPlannedArcV2Schema.optional()).describe("The character's planned development arc throughout the story."),
}).describe("Character schema defining their role and arc within a plot outline, used for plot generation and character extraction.");



// =============================================================================
// TYPE INFERENCE
// =============================================================================

export type CharacterSnapshotV2 = z.infer<typeof CharacterSnapshotV2Schema>;
export type CharacterV2 = z.infer<typeof CharacterV2Schema>;
export type CharacterContextV2 = z.infer<typeof CharacterContextV2Schema>;
export type CharacterBasicV2 = z.infer<typeof CharacterBasicV2Schema>;
export type CharacterRoleV2 = z.infer<typeof CharacterRoleV2Schema>;
export type CharacterRefV2 = z.infer<typeof CharacterRefV2Schema>;
